/* Módulo 2 · El directorio de los AI users y su ficha. Aparecen como personas
   porque lo son en el modelo: se les asigna, entregan, y su trabajo se mide.
   Lo editable es lo que los vuelve "los nuestros": las instrucciones de la casa. */
import { api, type AiUser } from "../api";
import { estado } from "../estado";
import { esc, modal, toast } from "../ui";
import type { Vista } from "./tipos";

const inicial = (n: string) => n.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

function ficha(u: AiUser, puedeEditar: boolean) {
  const señal = (n: number, t: string, color?: string) =>
    '<div><div style="font-size:17px;font-weight:640' + (color ? ";color:" + color : "") + '">' + n + '</div><div style="font-size:11px;color:var(--text3)">' + t + "</div></div>";
  return '<div class="card" style="padding:16px" data-ai="' + u.id + '">' +
    '<div style="display:flex;align-items:flex-start;gap:12px">' +
      '<div class="avatar" style="background:var(--accentSoft);color:var(--accent)">' + esc(inicial(u.nombre)) + "</div>" +
      '<div style="flex-grow:1;min-width:0">' +
        '<div style="font-size:14px;font-weight:640">' + esc(u.nombre) + ' <span class="tag" style="color:var(--accent);background:var(--accentSoft)">AI</span>' +
          (u.produce ? "" : ' <span class="tag" style="color:var(--text2);background:var(--surface3)">no produce piezas: critica</span>') + "</div>" +
        '<div style="font-size:12px;color:var(--text2);margin-top:3px">' + esc(u.oficio || "") + "</div>" +
        '<div style="font-size:11.5px;color:var(--text3);margin-top:5px">Le pesa más: ' + esc(u.conocimientoQuePesa || "—") + "</div>" +
      "</div>" +
    "</div>" +
    '<div style="display:flex;gap:26px;margin-top:14px;padding-top:12px;border-top:1px solid var(--linea)">' +
      señal(u.clientes, "clientes con acceso") + señal(u.entregables, "entregables") +
      señal(u.pendientesDeFirma, "esperando firma", u.pendientesDeFirma ? "var(--warn)" : undefined) +
      señal(u.rechazados, "rechazados", u.rechazados ? "var(--crit)" : undefined) +
    "</div>" +
    '<div style="margin-top:12px;padding-top:12px;border-top:1px solid var(--linea)">' +
      '<div style="display:flex;align-items:center"><div class="eyebrow">Instrucciones de la casa</div>' +
        (puedeEditar ? '<a data-editar="' + u.id + '" style="margin-left:auto;font-size:12px;cursor:pointer">Ajustar</a>' : "") + "</div>" +
      '<div style="font-size:12.5px;color:' + (u.instrucciones ? "var(--text2)" : "var(--text4)") + ';margin-top:5px;line-height:1.5">' +
        esc(u.instrucciones || "Sin instrucciones propias: produce con el criterio del perfil y lo que haya en el cerebro del cliente.") + "</div>" +
    "</div></div>";
}

export async function aiUsers(): Promise<Vista> {
  const a = estado.accesos();
  const puedeEditar = !!a.admin || (a.modulos as string[]).includes("settings");
  const lista = await api.aiUsers();
  const pendientes = lista.reduce((n, u) => n + u.pendientesDeFirma, 0);
  return {
    titulo: "Equipo AI",
    sub: lista.length + " especialistas no humanos · se les asigna un job como a cualquiera y nada de lo que producen avanza sin la firma de una persona",
    miga: ["Equipo AI"],
    html:
      (pendientes ? '<div class="aviso warn"><b>' + pendientes + " entregable(s) esperando firma.</b> Mientras nadie se haga responsable, no se entregan ni entran al cerebro. <a data-go=\"#/aprobaciones\" style=\"cursor:pointer\">Ir a firmar</a></div>" : "") +
      '<div class="grid2" style="gap:16px">' + lista.map((u) => ficha(u, puedeEditar)).join("") + "</div>" +
      '<div class="card" style="padding:15px 16px;margin-top:16px"><div class="eyebrow">Por qué son usuarios y no un modo de chat</div>' +
        '<div style="font-size:13px;color:var(--text2);margin-top:6px;line-height:1.6">Porque su trabajo se mide. Acumulan entregables, revisiones y tiempo igual que el de todos, y aparecen en la asignación asistida con las mismas señales. Uno que genera cinco rondas de revisión es un mal colaborador, y el sistema lo muestra con el mismo criterio que aplica a una persona.</div></div>',
    despues(root) {
      root.querySelectorAll<HTMLElement>("[data-editar]").forEach((el) => el.onclick = () => {
        const u = lista.find((x) => x.id === el.dataset.editar)!;
        modal({
          titulo: "Instrucciones para " + esc(u.nombre),
          sub: "Van por encima de su criterio en cada cosa que produce. Es lo que separa “nuestro copy” de un modelo genérico.",
          cuerpo: '<div class="lbl">INSTRUCCIONES</div><textarea id="ai-ins" rows="5" placeholder="Nuestro copy nunca usa signos de exclamación.">' + esc(u.instrucciones || "") + "</textarea>",
          botones: [{ t: "Cancelar" }, { t: "Guardar", cls: "primario", fn: async (m) => {
            const v = m.querySelector<HTMLTextAreaElement>("#ai-ins")!.value.trim();
            await api.ajustarAiUser(u.id, { instrucciones: v || null });
            toast("<b>Ajustado.</b> Aplica desde la próxima vez que produzca.");
            location.reload();
          } }],
        });
      });
    },
  };
}
